import { Component, OnDestroy, ViewEncapsulation, OnInit, ChangeDetectorRef, signal } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { Router, RoutesRecognized } from '@angular/router';
import { catchError, delay, expand, filter, map, take, takeUntil, tap } from 'rxjs/operators';
import { EMPTY, Subject, Subscription, of } from 'rxjs';
import { NavItem, isDefined } from '@slb-dls/angular-material/shared';
import { SlbNotificationItem } from '@slb-dls/angular-material/notifications-panel';
import { primaryNavItems } from './navigation-items';
import { MessageService, SlbSeverity } from '@slb-dls/angular-material/notification';
import { LoginService } from '@agora/agora-ui-library';
import { TimeZoneService } from './shared/services/time-zone.service';
import { TimeZone } from './shared/models/dashabordAlertData';
import { CameraProfileService } from './shared/services/camera-profile.service';
import { RequestId } from './shared/models/zone-configuration';
import { ZoneConfigurationConstants } from './shared/constants/zone-config.constant';
import { ZoneService } from './shared/services/zone.service';
import { UserSettingsService } from './shared/services/user-settings.service';
import { CUSTOM, METRIC } from './shared/constants/unit-constant';
import { UnitsData, UnitsGetData } from './shared/models/unit';
import { NotificationService } from './shared/services/notification.service';
import { NOTIFICATION } from './shared/constants/notification-constant';
import { NotificationData } from './shared/models/notification';
import { DashboardService } from './shared/services/dashboard.service';
import { DashboardConstants } from './shared/constants/dashboard-constants';
import { MatDialog } from '@angular/material/dialog';
import { SirenPopoverComponent } from './siren-popover/siren-popover.component';
import { SirenService } from './shared/services/siren.service';
import { SirenConstant } from './shared/constants/siren-constant';
import { SirenWorkflowUpdate } from './shared/models/siren';
import { Features } from './shared/models/feature';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AppComponent implements OnDestroy, OnInit {
  public notificationCount = 0;
  public notificationItems: SlbNotificationItem[] = [];
  public showBackButton: boolean;
  public showHeader = false;
  public pageTitle = '';
  public primaryNavItems: any;
  public options = ['EST', 'PST'];
  public cameraName: string;
  public refreshedDate = new Date();
  public currentTimezone = '';
  public refreshedTimezone = '';
  public userName!: string;
  public userEmail!: string;
  public currentZoneArea: string;
  public currentZoneTime: string;
  public country = 'All';
  public showCountry = false;
  public isCameraPage = false;
  public isCameraProfilePage: boolean;
  public disableZone = true;
  public cameraWorkflow: string[];
  public panelOpenState: boolean;
  public authorized: boolean;
  public cameraStatus: string;
  public gateway: string;
  public controllerId: string;
  public rigName: string;
  public disableRequestImage = signal(false);
  public isZoneDefinationScreen: boolean;
  public isAllEvents: boolean;
  public isAdmin: boolean;
  public routeUrl: string;
  public capacity = 10;
  public disableZoneBtn = signal(true);
  public init = 0;
  public delay = 10000;

  private routerSubscription = Subscription.EMPTY;
  private timezoneSubscription = Subscription.EMPTY;
  private reLoadSubscription = Subscription.EMPTY;
  private zoneSubscription = Subscription.EMPTY;
  private destroyApi = new Subject();
  private previousRoute: string;
  private feature: Features[];
  private destroy = new Subject();

  constructor(
    private router: Router,
    matIconRegistry: MatIconRegistry,
    domSanitizer: DomSanitizer,
    private messageService: MessageService,
    private loginService: LoginService,
    private timeZoneService: TimeZoneService,
    private cameraProfileService: CameraProfileService,
    private zoneService: ZoneService,
    private cd: ChangeDetectorRef,
    private userSettingService: UserSettingsService,
    private notificationService: NotificationService,
    private dashboardService: DashboardService,
    private sirenService: SirenService,
    private dialogRef: MatDialog
  ) {
    matIconRegistry.addSvgIconSet(domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/svg-symbols.svg'));
  }

  ngOnInit(): void {
    this.getFeature();
    this.panelOpenState = false;
    this.authorized = true;
    this.isAdmin = false;
    this.isAllEvents = false;
    this.showAllEvents();
    this.cameraProfileService.isAdmin$
      .pipe(
        tap(isAdminData => {
          this.isAdmin = isAdminData === 'true' ? true : false;
        })
      )
      .subscribe();
    this.disableRequestImage = this.zoneService.disableRequestImageButton;
    this.routerSubscription = this.router.events
      .pipe(
        filter(e => e instanceof RoutesRecognized),
        map(e => e as RoutesRecognized),
        tap(e => {
          this.onRouteChange(e);
        })
      )
      .subscribe();

    this.timezoneSubscription = this.timeZoneService.timeZoneDetails$
      .pipe(
        tap((timeZoneDetails: TimeZone) => {
          this.currentTimezone = timeZoneDetails.value;
          this.currentZoneArea = timeZoneDetails?.label;
          this.refreshedDate = this.timeZoneService.getDateandTime();
        })
      )
      .subscribe();

    this.loginService.isUserLoggedIn$
      .pipe(
        tap(() => {
          this.userEmail = this.loginService.getUserInfo()?.email ?? '';
          this.userName = this.loginService.getUserInfo()?.name ?? '';
          if (this.userName !== '') {
            this.initiateUnitSetting();
          }
        })
      )
      .subscribe();
    this.disableZoneBtn = this.cameraProfileService.disableZoneBtn;

    this.cameraProfileService.cameraWorkflow$.pipe(tap((value: string[]) => (this.cameraWorkflow = value))).subscribe();
    this.chooseSecondaryNav();
  }

  public primaryNavigationSelected(event: NavItem): void {
    if (event) {
      this.primaryNavItems.forEach((navItem: NavItem) => (navItem.active = navItem.routerLink === event.routerLink ? true : false));
      if (event?.routerLink) {
        this.router.navigateByUrl(event?.routerLink as unknown as string);
      }
    }
  }

  ngOnDestroy(): void {
    this.routerSubscription.unsubscribe();
    this.timezoneSubscription.unsubscribe();
    this.reLoadSubscription.unsubscribe();
    this.zoneSubscription.unsubscribe();
  }

  public openZoneConfigurationPopUp(): void {
    this.router.navigate(['zoneDefinition', this.cameraName, this.cameraStatus, this.gateway, this.controllerId, this.rigName]);
  }

  public logout(): void {
    this.dashboardService
      .userLogout()
      .pipe(
        take(1),
        tap(() => {
          this.loginService.logout();
          sessionStorage.clear();
        }),
        catchError(() => of(null))
      )
      .subscribe();
  }

  public navigateToSelf(): void {
    this.getNotificationData();
    const actualRoute = this.router.url;
    let defaultRoute = '/';
    if (this.router.url === '/home') {
      defaultRoute = '/camera';
    }
    this.router.navigateByUrl(defaultRoute, { skipLocationChange: true }).then(() => {
      this.router.navigate([actualRoute]);
    });
  }

  public requestImage(): void {
    this.zoneService.showRequestImageLoader.set(true);
    this.zoneService.disableRequestImageButton.set(true);
    this.zoneService
      .getRequestImage(this.gateway, this.controllerId)
      .pipe(
        tap((data: RequestId) => {
          if (data && data?.mediaRequestId) {
            this.zoneService.setRequestImageData(data);
          }
        }),
        catchError(() => {
          this.messageService.add({
            severity: SlbSeverity.Info,
            summary: 'Error',
            detail: ZoneConfigurationConstants.ZONEGETIMAGEERROR,
          });
          this.zoneService.showRequestImageLoader.set(false);
          this.zoneService.disableRequestImageButton.set(false);

          return of<string>('');
        }),
        takeUntil(this.destroyApi)
      )
      .subscribe();
  }

  public showAllEvents(): void {
    const showEvents = this.isAllEvents ? 'All' : '';
    sessionStorage.setItem('showAllEvents', showEvents);
    this.cameraProfileService.getEventData(true);
  }

  public redirectUserSettings(): void {
    const element: HTMLElement = document.getElementsByClassName('dls-logout-user-identity')[0] as HTMLElement;
    element.click();
    this.router.navigate(['userSettings']);
  }

  public notification(): void {
    if (this.router.url !== '/notification') {
      this.notificationCount = 0;
      this.router.navigate(['notification']);
    }
  }

  public openSirenPopup(): void {
    this.dialogRef.open(SirenPopoverComponent, {
      width: '550px',
      minHeight: '200px',
      disableClose: true,
      data: {
        controllerId: this.controllerId,
        cameraStatus: this.cameraStatus,
      },
    });
    this.destroy.next(true);
    this.sirenService.updateSirenDetails$.pipe(takeUntil(this.destroy)).subscribe(newSirenDetails => {
      if (newSirenDetails?.equipmentId) {
        this.init = 0;
        this.sirenService.setUpdateStatusMessage('inprogress');
        this.sirenService
          .saveSirenDetails(newSirenDetails)
          .pipe(
            tap(() => {
              setTimeout(() => {
                this.checkCommandStatus(newSirenDetails);
              }, this.delay);
            })
          )
          .subscribe();
      }
    });
  }

  private checkCommandStatus(requestPayload: SirenWorkflowUpdate): void {
    this.sirenService
      .getCommandStatusForCurrentWorkflows(requestPayload.equipmentId, false)
      .pipe(
        expand(res => {
          if (typeof res === 'string') {
            let updatedStatusText = '';
            let updatedStatusSuccess = false;
            if (res === 'Acknowledged') {
              updatedStatusText = SirenConstant.UPDATE_SUCCESS_MSG;
              updatedStatusSuccess = true;
            } else {
              updatedStatusText = SirenConstant.UPDATE_ERROR_MSG;
              updatedStatusSuccess = false;
            }
            this.messageService.add({
              severity: updatedStatusSuccess ? SlbSeverity.Success : SlbSeverity.Error,
              summary: updatedStatusText,
              closable: true,
              sticky: true,
            });
          }
          this.init += 1;
          if (
            res?.sirenStatus?.toLowerCase().includes('acknowledged') ||
            res?.sirenStatus?.toLowerCase().includes('timeout') ||
            res?.sirenStatus?.toLowerCase().includes('error')
          ) {
            this.sirenService.setUpdateStatusMessage('');
            if (res?.sirenStatus?.toLowerCase().includes('error')) {
              this.sirenService.setUpdateStatusMessage('error');
            }

            return of(res.sirenStatus);
          }
          if (res.sirenStatus) {
            this.sirenService.setUpdateStatusMessage('inprogress');

            return this.sirenService.getCommandStatusForCurrentWorkflows(requestPayload.equipmentId, this.init > 9).pipe(delay(this.delay));
          }
          this.sirenService.setUpdatedSirenDetails({ equipmentId: '', workFlowIds: [] });

          return EMPTY;
        })
      )
      .subscribe();
  }

  private getNotificationData(): void {
    this.notificationService
      .getUserNotificationCount()
      .pipe(
        tap((notificationData: number) => {
          this.notificationCount = 0;
          if (this.router.url !== NOTIFICATION.NOTIFICATION_URL) {
            this.notificationCount = notificationData;
          }
        }),
        catchError(() => {
          this.messageService.add({
            severity: SlbSeverity.Error,
            summary: NOTIFICATION.GETNOTIFICATIONDATAERROR,
            closable: true,
            sticky: true,
          });

          return of<NotificationData[]>({} as NotificationData[]);
        })
      )
      .subscribe();
  }

  private chooseSecondaryNav(): void {
    const url = document.location.href;
    if (url && !url.includes('login') && url.split('/')?.[3] !== '' && this.primaryNavItems?.length) {
      this.primaryNavItems.forEach((navItem: NavItem) => {
        let checkUrl = url.split('/')?.[3];
        if (url?.split('/')?.[3] === 'zoneDefinition') {
          checkUrl = 'home';
        }
        navItem.active = checkUrl === navItem.routerLink ? true : false;
      });
    }
  }

  /* eslint-disable max-lines-per-function */
  private onRouteChange(event: RoutesRecognized): void {
    this.isZoneDefinationScreen = false;
    this.panelOpenState = false;
    this.refreshedDate = this.timeZoneService.getDateandTime();
    this.showCountry = false;
    this.isCameraPage = false;
    this.isCameraProfilePage = false;
    this.country = 'All';
    const data: any = event?.state?.root?.firstChild?.data;
    this.showHeader = isDefined(data.showHeader) ? data.showHeader : true;
    this.showBackButton = isDefined(data.showBackButton) ? data.showBackButton : false;
    this.previousRoute = this.routeUrl;
    this.routeUrl = event.url;
    if (
      this.userName !== '' &&
      (!this.previousRoute ||
        this.previousRoute === '' ||
        this.previousRoute.includes('login') ||
        (this.previousRoute === NOTIFICATION.NOTIFICATION_URL && this.routeUrl !== NOTIFICATION.NOTIFICATION_URL))
    ) {
      this.getNotificationData();
    }
    if (this.routeUrl?.includes('eventid')) {
      this.routeUrl = decodeURIComponent(this.routeUrl);
      sessionStorage.setItem('eventlink', this.routeUrl);
      sessionStorage.setItem('currentUrl', this.routeUrl);
    }
    if (!this.routeUrl?.includes('login') && !this.routeUrl?.includes('unauthorized')) {
      sessionStorage.setItem('currentUrl', this.routeUrl);
    }
    /** When unauthorized page comes, needs to hide the secondary navigation items  */
    if (this.routeUrl?.includes('unauthorized') || this.routeUrl?.includes('unsubscribed')) {
      this.authorized = false;
      this.primaryNavItems = [];
    } else if (this.routeUrl?.includes('userSettings') && this.primaryNavItems?.length > 0) {
      const getActiveNav = this.primaryNavItems.filter((nav: { [x: string]: any }) => nav['active']);
      if (!getActiveNav.length) {
        this.featureFilter();
      } else {
        this.featureFilter();
      }
    } else {
      this.authorized = true;
      this.featureFilter();
    }
    if (data?.title?.toLowerCase() === 'camera' || data?.title === 'Zone Definition') {
      this.isCameraProfilePage = data?.title?.toLowerCase() === 'camera' ? true : false;
      this.panelOpenState = data?.title?.toLowerCase() === 'camera' ? true : false;
      const paramData = event.state.root.firstChild?.params;
      if (paramData?.cameraName) {
        this.cameraName = paramData.cameraName;
      }
      if (paramData?.cameraStatus) {
        this.cameraStatus = paramData.cameraStatus;
      }
      if (paramData?.gateway) {
        this.gateway = paramData.gateway;
      }
      if (paramData?.controllerId) {
        this.controllerId = paramData.controllerId;
      }
      if (paramData?.rigName) {
        if (typeof paramData.rigName === 'string') {
          this.rigName = decodeURIComponent(paramData.rigName).replace(/\*/g, ' ');
        }
      }
    } else {
      sessionStorage.removeItem('cameraName');
      this.cameraName = '';
      this.cameraWorkflow = [];
    }
    if (data?.title?.toLowerCase() === 'cameras') {
      this.isCameraPage = true;
      this.showCountry = true;
      const country = sessionStorage.getItem('country');
      if (country) {
        this.country = country === '' ? 'All' : country;
      }
      const item = this.primaryNavItems;
      this.primaryNavItems.forEach((navItem: NavItem) => (navItem.active = navItem.label?.toLowerCase() === 'camera'));
      this.primaryNavItems = [...item];
      this.cd.detectChanges();
    } else if (data?.title?.toLowerCase() === 'analytics') {
      const item = this.primaryNavItems;
      this.primaryNavItems.forEach((navItem: NavItem) => (navItem.active = navItem.label?.toLowerCase() === 'analytics'));
      this.primaryNavItems = [...item];
    } else if (data?.title?.toLowerCase() === 'report') {
      const item = this.primaryNavItems;
      this.primaryNavItems.forEach((navItem: NavItem) => (navItem.active = navItem.label?.toLowerCase() === 'report'));
      this.primaryNavItems = [...item];
    }
    this.pageTitle = data.title === 'Camera' ? this.cameraName : data.title;
    if (data?.title === 'Zone Definition') {
      this.isZoneDefinationScreen = true;
      const gatewayStatus = sessionStorage.getItem('gatewayStatus');
      this.zoneService.disableRequestImageButton.set(gatewayStatus !== '0' || this.cameraStatus !== '0' ? true : false);
      this.pageTitle = this.cameraName + ' - Zone definition setting';
    }
  }

  private constructUnits(data: UnitsGetData): void {
    if (data?.settingProperties) {
      let constructUnit = JSON.parse(JSON.stringify(data?.settingProperties)) as UnitsData;
      constructUnit.timeStamp = data?.createdAt;
      if (constructUnit?.unitName === CUSTOM) {
        constructUnit = this.userSettingService.createCustomUnits(constructUnit?.unitData, constructUnit.timeStamp);
      } else {
        constructUnit = this.userSettingService.createFinalUnits(constructUnit?.unitName, constructUnit.timeStamp);
      }
      this.userSettingService.setUnit(JSON.stringify(constructUnit));
    }
  }

  private initiateUnitSetting(): void {
    this.userSettingService
      .getLatestUnits()
      .pipe(
        tap((data: UnitsGetData) => {
          if (data?.settingProperties) {
            this.constructUnits(data);
          } else {
            this.setInitialUnit();
          }
        }),
        catchError(() => {
          this.setInitialUnit();

          return of<UnitsData>({} as UnitsData);
        })
      )
      .subscribe();
  }

  private getFeature(): void {
    this.dashboardService
      .getFeature()
      .pipe(
        tap((feature: Features[]) => {
          this.feature = feature;
          sessionStorage.setItem('feature', JSON.stringify(this.feature));
          this.featureFilter();
        }),
        catchError(() => {
          this.messageService.add({
            severity: SlbSeverity.Error,
            summary: DashboardConstants.dashabordCountryAPIError,
            closable: true,
            sticky: true,
          });

          return of<string[]>([] as string[]);
        })
      )
      .subscribe();
  }
  private featureFilter(): void {
    const feature: string[] = (this.feature ?? []).map(f => f.featureType);
    if (!feature.map((f: string) => f.toLowerCase()).includes('report')) {
      this.primaryNavItems = primaryNavItems.filter(item => item.label !== 'Report');
    } else {
      this.primaryNavItems = [...primaryNavItems];
    }
  }

  private setInitialUnit(): void {
    const unit = this.userSettingService.createFinalUnits(METRIC, '');
    sessionStorage.setItem('unit', JSON.stringify(unit));
    this.userSettingService.setUnit(JSON.stringify(unit));
  }
}
